import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  CheckCircle as SettledIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useMyAccountingBalances, useAuth } from '../../hooks';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { PlayerAvatar, PageHeader } from '../../components/shared';
import { BalancesEducationModal } from '../../components/education';
import { hasSeenEducationModal } from '../../lib/educationModals';
import type { ManagerBalance } from '../../types';

type BalanceFilter = 'all' | 'owes' | 'owed' | 'settled';

export const MyBalancesPage = () => {
  useDocumentTitle('My Balances');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { summary, isLoading, error } = useMyAccountingBalances();
  const [filter, setFilter] = useState<BalanceFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => getStoredRowsPerPage());
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('balances'));

  // Filter out the current user from the managers list (don't show yourself as a manager)
  const allBalances = useMemo(() => {
    if (!summary?.managerBalances) return [];
    return summary.managerBalances.filter(balance => balance.managerId !== user?.id);
  }, [summary?.managerBalances, user?.id]);

  // Calculate historical performance stats across all managers
  const performanceStats = useMemo(() => {
    const totalSpent = allBalances.reduce((sum, b) => sum + b.totalOwed, 0);
    const totalWinnings = allBalances.reduce((sum, b) => sum + b.totalEarned, 0);
    const overallPerformance = totalWinnings - totalSpent;
    return { totalSpent, totalWinnings, overallPerformance };
  }, [allBalances]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary || summary.completedBoardsCount === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="My Balances"
          helpLink="/support/accounting#player-view"
          helpTooltip="Learn more about balances"
        />
        <Alert severity="info">
          You don't have any completed boards yet. Balances are only available for completed boards.
          Once a board you've participated in is completed, you'll be able to see your balances here.
        </Alert>
      </Box>
    );
  }

  // Apply filter
  const filteredBalances = allBalances.filter(balance => {
    switch (filter) {
      case 'owes':
        return balance.netBalance < 0 && !balance.fullySettled;
      case 'owed':
        return balance.netBalance > 0 && !balance.fullySettled;
      case 'settled':
        return balance.fullySettled;
      default:
        return true;
    }
  });

  // Calculate counts for filter badges
  const owesCount = allBalances.filter(b => b.netBalance < 0 && !b.fullySettled).length;
  const owedCount = allBalances.filter(b => b.netBalance > 0 && !b.fullySettled).length;
  const settledCount = allBalances.filter(b => b.fullySettled).length;

  // Sort balances: unsettled first (by absolute balance), then settled
  const sortedBalances = [...filteredBalances].sort((a, b) => {
    if (a.fullySettled !== b.fullySettled) {
      return a.fullySettled ? 1 : -1;
    }
    return Math.abs(b.netBalance) - Math.abs(a.netBalance);
  });

  const handleRowClick = (balance: ManagerBalance) => {
    navigate(`/balances/manager/${balance.managerId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="My Balances"
        subtitle="View your balances across all board managers you've played with."
        helpLink="/support/accounting#player-view"
        helpTooltip="Learn more about balances"
      />

      {/* Summary Stats */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Completed Boards</Typography>
            <Typography variant="h6">{summary.completedBoardsCount}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">You Owe</Typography>
            <Typography variant="h6" color="error.main">
              ${summary.totalOwedToManagers.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Owed to You</Typography>
            <Typography variant="h6" color="success.main">
              ${summary.totalOwedByManagers.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Total Managers</Typography>
            <Typography variant="h6">{allBalances.length}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Historical Performance */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Historical Performance</Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TrendingDownIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="overline" color="text.secondary">Total Spent</Typography>
              <Typography variant="h6">
                ${performanceStats.totalSpent.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TrendingUpIcon sx={{ color: 'info.main', mt: 0.5 }} />
            <Box>
              <Typography variant="overline" color="text.secondary">Total Winnings</Typography>
              <Typography variant="h6" color="info.main">
                ${performanceStats.totalWinnings.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Overall Performance (Winnings - Cost)</Typography>
            <Typography
              variant="h6"
              color={performanceStats.overallPerformance >= 0 ? 'success.main' : 'error.main'}
            >
              {performanceStats.overallPerformance >= 0 ? '+' : '-'}${Math.abs(performanceStats.overallPerformance).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Quick Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`All (${allBalances.length})`}
          onClick={() => { setFilter('all'); setPage(0); }}
          color={filter === 'all' ? 'primary' : 'default'}
          variant={filter === 'all' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`I Owe (${owesCount})`}
          onClick={() => { setFilter('owes'); setPage(0); }}
          color={filter === 'owes' ? 'error' : 'default'}
          variant={filter === 'owes' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Owed to Me (${owedCount})`}
          onClick={() => { setFilter('owed'); setPage(0); }}
          color={filter === 'owed' ? 'success' : 'default'}
          variant={filter === 'owed' ? 'filled' : 'outlined'}
        />
        <Chip
          label={`Settled (${settledCount})`}
          onClick={() => { setFilter('settled'); setPage(0); }}
          color={filter === 'settled' ? 'info' : 'default'}
          variant={filter === 'settled' ? 'filled' : 'outlined'}
        />
      </Box>

      {sortedBalances.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          {filter === 'all' ? (
            <>
              <SettledIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                You're all clear!
              </Typography>
              <Typography color="text.secondary">
                You don't have any outstanding balances with board managers.
              </Typography>
            </>
          ) : filter === 'owes' ? (
            <>
              <SettledIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nothing owed!
              </Typography>
              <Typography color="text.secondary">
                You don't owe any managers right now.
              </Typography>
            </>
          ) : filter === 'owed' ? (
            <>
              <Typography variant="h6" gutterBottom color="text.secondary">
                No pending payouts
              </Typography>
              <Typography color="text.secondary">
                No managers currently owe you money.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom color="text.secondary">
                No settled balances
              </Typography>
              <Typography color="text.secondary">
                You don't have any fully settled manager relationships yet.
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Manager</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Boards</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Owed</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Paid</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 100 }}>Winnings</th>
                  <th style={{ textAlign: 'right', padding: '12px', fontWeight: 600, width: 120 }}>Net Balance</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 80 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedBalances
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((balance) => (
                    <tr
                      key={balance.managerId}
                      onClick={() => handleRowClick(balance)}
                      style={{
                        backgroundColor: balance.fullySettled ? '#e8f5e9' : '#fff',
                        borderBottom: '1px solid #e0e0e0',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Manager */}
                      <td style={{ padding: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PlayerAvatar
                            displayName={balance.manager?.displayName || 'Unknown'}
                            handle={balance.manager?.handle}
                            iconName={balance.manager?.iconName}
                            size="small"
                            showBadge={false}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {balance.manager?.displayName || 'Unknown'}
                          </Typography>
                        </Box>
                      </td>

                      {/* Board Count */}
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <Typography variant="body2">
                          {balance.boardCount}
                        </Typography>
                      </td>

                      {/* Total Owed */}
                      <td style={{ textAlign: 'right', padding: '12px' }}>
                        <Typography variant="body2" color="text.secondary">
                          ${balance.totalOwed.toFixed(2)}
                        </Typography>
                      </td>

                      {/* Total Paid */}
                      <td style={{ textAlign: 'right', padding: '12px' }}>
                        <Typography variant="body2" color="success.main" fontWeight={balance.totalPaid > 0 ? 'bold' : 'normal'}>
                          ${balance.totalPaid.toFixed(2)}
                        </Typography>
                      </td>

                      {/* Total Winnings */}
                      <td style={{ textAlign: 'right', padding: '12px' }}>
                        <Typography variant="body2" color="info.main" fontWeight={balance.totalEarned > 0 ? 'bold' : 'normal'}>
                          ${balance.totalEarned.toFixed(2)}
                        </Typography>
                      </td>

                      {/* Net Balance */}
                      <td style={{ textAlign: 'right', padding: '12px' }}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={balance.netBalance >= 0 ? 'success.main' : 'error.main'}
                        >
                          {balance.netBalance >= 0 ? '+' : '-'}${Math.abs(balance.netBalance).toFixed(2)}
                        </Typography>
                      </td>

                      {/* Status */}
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        {balance.fullySettled ? (
                          <Tooltip title="Fully settled">
                            <SettledIcon sx={{ color: 'success.main' }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="warning.main">
                            Pending
                          </Typography>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Box>
          <TablePagination
            component="div"
            count={sortedBalances.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}

      <BalancesEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
};

export default MyBalancesPage;
